import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Avatar, Chip, Stack, IconButton, Tooltip } from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import { useParams } from 'react-router-dom';
import { API_BASE } from '../config';

export default function TeamProfile() {
  const { slug } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetch(`${API_BASE}/api/team/slug/${encodeURIComponent(slug)}`)
      .then(res => {
        if (res.status === 404) return null;
        return res.ok ? res.json() : Promise.reject(res);
      })
      .then(obj => { if (mounted) setMember(obj); })
      .catch(err => { console.error('Failed to load team member', err); if (mounted) setMember(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [slug]);

  if (loading) return (<Container sx={{ py: 6 }}><Typography>Loading...</Typography></Container>);
  if (!member) return (<Container sx={{ py: 6 }}><Typography variant="h4">Team member not found</Typography></Container>);

  return (
    <Container sx={{ py: 6 }}>
      {(() => {
        const name = `${member.firstName || member.name || ''} ${member.lastName || ''}`.trim() || member.name || '';
        const role = member.designation || member.role || '';
        const avatarSrc = member.id ? `${API_BASE}/api/team/${member.id}/photo` : null;
        // prefer the full experience text for the profile, fall back to productionDetails
        const bio = member.bioLong || member.experienceDetails || member.productionDetails || '';
        const parseTags = (text) => {
          if (!text) return [];
          return text.split(/[\n,;|]+/).map(s => s.trim()).filter(Boolean).slice(0, 6);
        };
        const tags = Array.isArray(member.skills) ? member.skills : parseTags(member.productionDetails || member.experienceDetails || member.bioLong || member.bio);

        return (
          <>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <Avatar src={avatarSrc} sx={{ width: 120, height: 120 }}>{!avatarSrc && (name ? name.split(' ').map(n=>n[0]).join('').slice(0,2) : '?')}</Avatar>
              <Box>
                <Typography variant="h4">{name}</Typography>
                <Typography color="text.secondary">{role}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  {(tags || []).map(s => <Chip key={s} label={s} size="small" />)}
                  {member.social?.linkedin && (
                    <Tooltip title="LinkedIn">
                      <IconButton
                        component="a"
                        href={member.social.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`LinkedIn profile of ${name}`}
                      >
                        <LinkedInIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {member.social?.twitter && (
                    <Tooltip title="Twitter">
                      <IconButton
                        component="a"
                        href={member.social.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Twitter profile of ${name}`}
                      >
                        <TwitterIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Box>
            </Box>

            <Box sx={{ mt: 4 }}>
              <Typography>{bio}</Typography>
            </Box>
          </>
        );
      })()}
    </Container>
  );
}
